import { FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { catalogService } from '@/services';
import { useSettingsStore } from '@/stores';
import { Badge, Button, PriceBlock, SearchBar } from '@/components/ui';
import {
  HeroBanner,
  PromoBanner,
  RecommendedProducts,
  BentoGrid,
  BentoTile,
} from '@/components/home';
import type { Printing, TcgCardImage } from '@/types';
import { formatPrice } from '@/utils/format';
import { CatalogImage } from '@/features/catalog/components/CatalogImage';
import { commonStockCountLabel, homeSaleCountLabel } from '@/features/catalog/utils/stockDisplay';

/** Test images (placeholder service). Reemplazar por arte real en public/banners y public/promos. */
const TEST_IMG = {
  hero: 'https://picsum.photos/seed/stonehartden-hero/1920/640',
  promo: 'https://picsum.photos/seed/stonehartden-promo/1400/300',
  bentoNew: 'https://picsum.photos/seed/stonehartden-new/800/600',
  bentoSets: 'https://picsum.photos/seed/stonehartden-sets/600/900',
  bentoPremium: 'https://picsum.photos/seed/stonehartden-premium/800/600',
} as const;

const HOME_TCG_TAKE = 24;
const HOME_TCG_VISIBLE = 6;
const PLACEHOLDER_CARD_IMAGE = 'https://via.placeholder.com/240x336?text=Yu-Gi-Oh%21';

type Money = {
  price: number | string;
  currency: string;
  priceCop?: number | string | null;
  priceUsd?: number | string | null;
};

function firstCardImageUrl(images?: TcgCardImage[] | null): string {
  const image = images?.[0];
  return (
    image?.imageUrl ||
    image?.imageUrlSmall ||
    image?.imageUrlCropped ||
    image?.image_url ||
    image?.image_url_small ||
    image?.image_url_cropped ||
    PLACEHOLDER_CARD_IMAGE
  );
}

function getPrintingDetailUrl(printing: Printing): string {
  const rarityValue = printing.setRarityCode || printing.setRarity || '';
  return `/yugioh/cards/${printing.card.id}?setCode=${encodeURIComponent(
    printing.setCode || '',
  )}&setRarityCode=${encodeURIComponent(rarityValue)}&setEdition=${encodeURIComponent(
    printing.setEdition || '',
  )}`;
}

function getPrintingInfo(printing: Printing): string {
  return [printing.setCode, printing.setRarityCode || printing.setRarity, printing.setEdition]
    .filter(Boolean)
    .join(' - ');
}

function getListingStock(printing: Printing): number {
  return (
    printing.aggregatedStock?.aggregatedStock ??
    printing.listings?.reduce((total, listing) => total + (listing.stock ?? 0), 0) ??
    0
  );
}

function getListingCount(printing: Printing): number {
  return printing.aggregatedStock?.listingCount ?? printing.listings?.length ?? 0;
}

function getLocalPrice(printing: Printing): Money | null {
  if (printing.fromPrice) {
    return {
      price: printing.fromPrice.price,
      currency: printing.fromPrice.currency,
      priceCop: printing.fromPrice.priceCop,
      priceUsd: printing.fromPrice.priceUsd,
    };
  }

  const activeListing = [...(printing.listings ?? [])]
    .filter((listing) => listing.stock > 0)
    .sort((a, b) => Number(a.price) - Number(b.price))[0];

  return activeListing
    ? {
        price: activeListing.price,
        currency: activeListing.currency,
        priceCop: activeListing.priceCop,
        priceUsd: activeListing.priceUsd,
      }
    : null;
}

function getMarketPrice(printing: Printing): Money | null {
  if (printing.setPriceLow != null) {
    return {
      price: printing.setPriceLow,
      currency: printing.setPriceLowCurrency || printing.setPriceCurrency || 'COP',
      priceCop: printing.setPriceLowCurrency === 'COP' ? printing.setPriceLow : undefined,
      priceUsd: printing.setPriceLowUsd ?? (printing.setPriceLowCurrency === 'USD' ? printing.setPriceLow : undefined),
    };
  }
  if (printing.setPrice != null) {
    return {
      price: printing.setPrice,
      currency: printing.setPriceCurrency || 'COP',
      priceCop: printing.setPriceCurrency === 'COP' ? printing.setPrice : undefined,
      priceUsd: printing.setPriceUsd ?? (printing.setPriceCurrency === 'USD' ? printing.setPrice : undefined),
    };
  }
  return null;
}

function formatUsdSecondary(value?: number | string | null): string | undefined {
  if (value == null) return undefined;
  return `USD ${formatPrice(value, 'USD').replace(/^USD\s+/, '')}`;
}

function formatDualPrice(price: Money | null): { amount: string; secondary?: string } | null {
  if (!price) return null;
  const currency = price.currency?.toUpperCase();
  const cop = currency === 'COP' ? price.price : price.priceCop;
  const usd = currency === 'USD' ? price.price : price.priceUsd;

  return {
    amount: cop != null ? formatPrice(cop, 'COP') : formatPrice(price.price, price.currency),
    secondary: formatUsdSecondary(usd),
  };
}

function isPrintingForSale(printing: Printing): boolean {
  return (
    printing.availability.isAvailable &&
    getListingStock(printing) > 0 &&
    getListingCount(printing) > 0
  );
}

export function HomePage(): JSX.Element {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const currency = useSettingsStore((s) => s.currency);
  const [search, setSearch] = useState('');

  const { data: products = [] } = useQuery({
    queryKey: ['products', 'featured', currency],
    queryFn: () => catalogService.getActiveProducts({ take: 12, currency }),
    staleTime: 30 * 1000,
  });

  const { data: homeTcgPrintings = [], isLoading: isHomeTcgLoading } = useQuery({
    queryKey: ['home', 'tcg-printings', currency],
    queryFn: () =>
      catalogService.getPrintings({
        take: HOME_TCG_TAKE,
        currency,
      }),
    staleTime: 30 * 1000,
  });

  const latestCardsForSale = useMemo(
    () => homeTcgPrintings.filter(isPrintingForSale).slice(0, HOME_TCG_VISIBLE),
    [homeTcgPrintings],
  );

  const catalogCards = useMemo(
    () => homeTcgPrintings.slice(0, HOME_TCG_VISIBLE),
    [homeTcgPrintings],
  );

  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    const q = search.trim();
    navigate(q ? `/products?q=${encodeURIComponent(q)}` : '/products');
  };

  return (
    <div className="flex w-full flex-col gap-8">
      <HeroBanner
        eyebrow={t('home.hero.eyebrow')}
        title={t('home.hero.title')}
        description={t('home.hero.description')}
        ctaLabel={t('home.hero.cta')}
        ctaHref="/products"
        imageSrc={TEST_IMG.hero}
      />

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <form onSubmit={onSearchSubmit} className="mb-6">
          <SearchBar
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('home.searchPlaceholder')}
            aria-label={t('common.search')}
          />
        </form>

        <PromoBanner
          className="mb-8"
          variant="dark"
          title={t('home.promo.title')}
          description={t('home.promo.description')}
          ctaLabel={t('home.promo.cta')}
          ctaHref="/products"
          imageSrc={TEST_IMG.promo}
        />

        <section className="mb-10">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-sans text-xs font-semibold uppercase text-brand-700">
                {t('home.bento.eyebrow')}
              </p>
              <h2 className="font-heading text-heading-lg text-brand-950">
                {t('home.bento.title')}
              </h2>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/products')}>
              {t('home.bento.allProducts')}
            </Button>
          </div>
          <BentoGrid>
            <BentoTile
              title={t('home.bento.newTitle')}
              description={t('home.bento.newDesc')}
              href="/products"
              size="wide"
              tone="dark"
              imageSrc={TEST_IMG.bentoNew}
              ctaLabel={t('home.bento.cta')}
            />
            <BentoTile
              title={t('home.bento.cardsForSaleTitle')}
              description={t('home.bento.cardsForSaleDesc')}
              href="/products?type=tcg"
              size="medium"
              tone="teal"
              ctaLabel={t('home.bento.cta')}
            />
            <BentoTile
              title={t('home.bento.cardCatalogTitle')}
              description={t('home.bento.cardCatalogDesc')}
              href="/yugioh"
              size="medium"
              tone="ice"
              ctaLabel={t('home.bento.cta')}
            />
            <BentoTile
              title={t('home.bento.setsTitle')}
              description={t('home.bento.setsDesc')}
              href="/yugioh"
              size="tall"
              tone="nav"
              imageSrc={TEST_IMG.bentoSets}
              ctaLabel={t('home.bento.cta')}
            />
            <BentoTile
              title={t('home.bento.catsTitle')}
              description={t('home.bento.catsDesc')}
              href="/products"
              size="small"
              tone="neutral"
              ctaLabel={t('home.bento.cta')}
            />
            <BentoTile
              title={t('home.bento.communityTitle')}
              description={t('home.bento.communityDesc')}
              href="/products"
              size="small"
              tone="nav"
              ctaLabel={t('home.bento.cta')}
            />
            <BentoTile
              title={t('home.bento.premiumTitle')}
              description={t('home.bento.premiumDesc')}
              href="/products"
              size="wide"
              tone="dark"
              imageSrc={TEST_IMG.bentoPremium}
              ctaLabel={t('home.bento.cta')}
            />
          </BentoGrid>
        </section>

        <section className="mb-10">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-heading text-heading-lg text-brand-950">
                {t('home.tcg.latestTitle')}
              </h2>
              <p className="mt-0.5 font-sans text-sm text-brand-700">
                {t('home.tcg.latestDescription')}
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/products?type=tcg')}>
              {t('home.tcg.shopCta')}
            </Button>
          </div>

          {isHomeTcgLoading ? (
            <div className="rounded-lg border border-brand-700/15 bg-surface px-4 py-8 text-center font-sans text-sm text-brand-700">
              {t('home.tcg.loading')}
            </div>
          ) : latestCardsForSale.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {latestCardsForSale.map((printing, index) => {
                const localPrice = getLocalPrice(printing);
                const marketPrice = getMarketPrice(printing);
                const price = localPrice ?? marketPrice;
                const priceDisplay = formatDualPrice(price);
                const stock = getListingStock(printing);
                const listingCount = getListingCount(printing);

                return (
                  <Link
                    key={`latest-${printing.card.id}-${printing.setCode ?? ''}-${printing.id}-${index}`}
                    to={getPrintingDetailUrl(printing)}
                    className="group grid min-h-[12.5rem] grid-cols-[100px_minmax(0,1fr)] overflow-hidden rounded-lg border border-brand-700/15 bg-surface shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                  >
                    <div className="flex h-full min-h-[12.5rem] items-center justify-center overflow-hidden bg-brand-100">
                      <CatalogImage
                        src={firstCardImageUrl(printing.card.images)}
                        alt={printing.card.name}
                        className="h-full w-full object-contain transition-transform group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="flex min-w-0 flex-col gap-1.5 p-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant="success">
                          {homeSaleCountLabel(t, stock)}
                        </Badge>
                        <Badge variant="brand">
                          {t('home.tcg.listingCount', { count: listingCount })}
                        </Badge>
                      </div>
                      <div>
                        <h3 className="line-clamp-2 font-heading text-sm font-semibold text-brand-950">
                          {printing.card.name}
                        </h3>
                        <p className="mt-0.5 line-clamp-2 font-sans text-xs text-brand-700">
                          {getPrintingInfo(printing)}
                        </p>
                      </div>
                      <PriceBlock
                        className="mt-auto"
                        amount={priceDisplay?.amount ?? t('home.tcg.priceUnavailable')}
                        secondary={priceDisplay?.secondary ?? (localPrice ? t('home.tcg.storeFrom') : t('home.tcg.marketReference'))}
                        size="sm"
                      />
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-brand-700/15 bg-surface px-4 py-8 text-center font-sans text-sm text-brand-700">
              {t('home.tcg.latestEmpty')}
            </div>
          )}
        </section>

        <div className="mb-10">
          <RecommendedProducts
            products={products}
            title={t('home.featured')}
            emptyLabel={t('home.featuredEmpty')}
            inStockLabel={t('common.inStock')}
            outOfStockLabel={t('common.outOfStock')}
            stockLabel={(product) => commonStockCountLabel(t, product.stock ?? 0)}
            onProductClick={(id) => navigate(`/products/${id}`)}
          />
        </div>

        <section className="mb-4">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-heading text-heading-lg text-brand-950">
                {t('home.tcg.catalogTitle')}
              </h2>
              <p className="mt-0.5 font-sans text-sm text-brand-700">
                {t('home.tcg.catalogDescription')}
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/yugioh')}>
              {t('home.tcg.catalogCta')}
            </Button>
          </div>

          {isHomeTcgLoading ? (
            <div className="rounded-lg border border-brand-700/15 bg-surface px-4 py-8 text-center font-sans text-sm text-brand-700">
              {t('home.tcg.loading')}
            </div>
          ) : catalogCards.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {catalogCards.map((printing, index) => {
                const isForSale = isPrintingForSale(printing);
                const localPrice = getLocalPrice(printing);
                const marketPrice = getMarketPrice(printing);
                const price = localPrice ?? marketPrice;
                const priceDisplay = formatDualPrice(price);

                return (
                  <Link
                    key={`catalog-${printing.card.id}-${printing.setCode ?? ''}-${printing.id}-${index}`}
                    to={getPrintingDetailUrl(printing)}
                    className="group grid min-h-[12.5rem] grid-cols-[100px_minmax(0,1fr)] overflow-hidden rounded-lg border border-brand-700/15 bg-surface shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                  >
                    <div className="flex h-full min-h-[12.5rem] items-center justify-center overflow-hidden bg-brand-100">
                      <CatalogImage
                        src={firstCardImageUrl(printing.card.images)}
                        alt={printing.card.name}
                        className="h-full w-full object-contain transition-transform group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="flex min-w-0 flex-col gap-1.5 p-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant={isForSale ? 'success' : 'neutral'}>
                          {isForSale
                            ? homeSaleCountLabel(t, getListingStock(printing))
                            : t('home.tcg.noStock')}
                        </Badge>
                        <Badge variant="brand">{t('home.tcg.informative')}</Badge>
                      </div>
                      <div>
                        <h3 className="line-clamp-2 font-heading text-sm font-semibold text-brand-950">
                          {printing.card.name}
                        </h3>
                        <p className="mt-0.5 line-clamp-2 font-sans text-xs text-brand-700">
                          {getPrintingInfo(printing)}
                        </p>
                      </div>
                      <PriceBlock
                        className="mt-auto"
                        amount={priceDisplay?.amount ?? t('home.tcg.priceUnavailable')}
                        secondary={priceDisplay?.secondary ?? (localPrice ? t('home.tcg.storeFrom') : t('home.tcg.marketReference'))}
                        size="sm"
                      />
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-brand-700/15 bg-surface px-4 py-8 text-center font-sans text-sm text-brand-700">
              {t('home.tcg.catalogEmpty')}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
